import { z } from 'zod';
import { FormatSchema } from './format';

const id = z.string().min(1).max(80);
const timestamp = z.number().int().nonnegative();
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-03.');

export const TournamentSchema = z.object({
  id,
  name: z.string().trim().min(1).max(80),
  /** Format new rounds start with (event × rules, e.g. "parli-chssa"). Each round can still pick its own. */
  formatId: z.string().max(40).optional(),
  /** Local calendar days (YYYY-MM-DD). Optional: they sort the dashboard into current / upcoming / previous. */
  startDate: isoDay.optional(),
  endDate: isoDay.optional(),
  createdAt: timestamp,
  updatedAt: timestamp,
}).refine((t) => !t.startDate || !t.endDate || t.endDate >= t.startDate, { message: 'The last day must be on or after the first day.', path: ['endDate'] });

export const TeamSchema = z.object({
  code: z.string().trim().min(1).max(40),
  school: z.string().trim().max(80).default(''),
});

export const RoundStatus = z.enum(['scheduled', 'inRound', 'deciding', 'committed', 'submitted']);

export const RoundSchema = z.object({
  id,
  tournamentId: id,
  label: z.string().trim().min(1).max(20),
  room: z.string().trim().max(30).default(''),
  flight: z.string().trim().max(4).default(''),
  /** "HH:MM" local time, optional. */
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Start time must look like 09:30.').or(z.literal('')).default(''),
  teamA: TeamSchema,
  teamB: TeamSchema,
  /** Which side (index into format.sideLabels) team A is on. null = not decided yet. */
  teamASide: z.union([z.literal(0), z.literal(1)]).nullable(),
  /** Which side speaks first. null = not decided yet. */
  firstSide: z.union([z.literal(0), z.literal(1)]).nullable(),
  /** The topic being debated ("Resolved: …"). Parli's changes every round; other events keep one per tournament. */
  resolution: z.string().trim().max(1000, 'A resolution can be up to 1,000 characters.').default(''),
  /** Snapshot: editing a format later never changes a past round. */
  format: FormatSchema,
  status: RoundStatus,
  /** Descriptive AI recap, written only after the ballot is submitted. Team codes restored; names stay redacted. */
  summary: z.object({ text: z.string().max(6000), model: z.string().max(60), createdAt: timestamp }).nullable().optional(),
  createdAt: timestamp,
  updatedAt: timestamp,
});

/** Debater names are PII of minors: stored in their own device-local table, never synced, purged after submission. */
export const RoundNamesSchema = z.object({
  roundId: id,
  teamA: z.array(z.string().trim().max(60)).max(5),
  teamB: z.array(z.string().trim().max(60)).max(5),
  purgeAfter: timestamp.nullable(),
});

export const Mark = z.enum(['extended', 'dropped', 'turn', 'conceded', 'star', 'unclear']);

export const FlowRowSchema = z.object({
  id,
  roundId: id,
  sheetId: z.string().min(1),
  rank: z.number(),
  /** Short name for the argument that starts on this row, for jumping around the flow. */
  title: z.string().trim().max(60).optional(),
  /** 'ai' titles may be replaced automatically; the judge's own never are. */
  titleBy: z.enum(['ai', 'judge']).optional(),
  createdAt: timestamp,
  /** Last change (e.g. a new title). Optional: rows saved before sync existed only have createdAt. */
  updatedAt: timestamp.optional(),
});

export const FlowCellSchema = z.object({
  /** Deterministic: `${rowId}~${segmentId}` so there is exactly one box per (row, column). */
  id,
  roundId: id,
  rowId: id,
  segmentId: z.string().min(1),
  text: z.string().max(4000, 'A single box can hold up to 4,000 characters.'),
  marks: z.array(Mark).max(6),
  updatedAt: timestamp,
});

export const ClockSchema = z.object({
  budgetMs: z.number().int().nonnegative(),
  usedMs: z.number().int(),
  runningSince: timestamp.nullable(),
});

export const TimerLogEntrySchema = z.object({
  at: timestamp,
  what: z.string().max(120),
});

export const TimerStateSchema = z.object({
  cursor: z.number().int().nonnegative(),
  speech: ClockSchema,
  prep: z.tuple([ClockSchema, ClockSchema]),
  running: z.enum(['speech', 'prep0', 'prep1']).nullable(),
  /** Actual time used per segment id once "done". */
  actuals: z.record(z.string(), z.number().int()),
  log: z.array(TimerLogEntrySchema).max(500),
});

export const TimerRecordSchema = z.object({
  roundId: id,
  state: TimerStateSchema,
  /** Previous states for undo (most recent last). */
  undo: z.array(TimerStateSchema).max(30),
  updatedAt: timestamp,
});

export const Survival = z.enum(['alive', 'dead', 'unsure']);

export const SpeakerNoteSchema = z.object({
  text: z.string().max(4000, 'A speaker note can hold up to 4,000 characters.'),
  tags: z.array(z.string().max(40)).max(20),
});
export type SpeakerNote = z.infer<typeof SpeakerNoteSchema>;

/** One argument on the AI's map of the flow. `row` is the argument's head row id. Descriptive only: no winner. */
export const MapArgumentSchema = z.object({
  row: z.string(),
  side: z.string().max(20),
  title: z.string().max(120),
  claim: z.string().max(600),
  engaged: z.array(z.object({
    speech: z.string().max(12),
    row: z.string().nullable(),
    response: z.string().max(600),
    answered: z.enum(['yes', 'no', 'partly', 'unclear']),
    answeredIn: z.string().max(12).nullable(),
    /** The judge's correction: moved here from another argument, or marked as not about this argument. */
    judge: z.enum(['moved', 'dismissed']).optional(),
  })).max(40),
  extended: z.array(z.object({ speech: z.string().max(12), present: z.boolean(), row: z.string().nullable() })).max(12),
  weighed: z.array(z.object({ speech: z.string().max(12), text: z.string().max(600) })).max(12),
  status: z.enum(['alive', 'dead', 'contested', 'unclear']),
  why: z.string().max(600),
});
export type MapArgument = z.infer<typeof MapArgumentSchema>;

export const ChatMessageSchema = z.object({ role: z.enum(['judge', 'assistant']), text: z.string().max(8000), at: timestamp });
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const BallotSchema = z.object({
  roundId: id,
  assessments: z.record(z.string(), z.object({ survival: Survival, note: z.string().max(500).default('') })),
  weighing: z.object({
    side0: z.array(z.string()).max(6),
    side1: z.array(z.string()).max(6),
    winner: z.enum(['side0', 'side1', 'none']).nullable(),
    note: z.string().max(1000).default(''),
  }),
  winnerSide: z.union([z.literal(0), z.literal(1)]).nullable(),
  committedAt: timestamp.nullable(),
  rfd: z.string().max(8000),
  /** Keyed "A0", "A1", "B0"… (team + speaker index). */
  points: z.record(z.string(), z.number().nullable()),
  teamComments: z.object({ A: z.string().max(8000), B: z.string().max(8000) }),
  speakerComments: z.record(z.string(), z.string().max(4000)),
  /** Private notes per speaker ("A0", "B1"…) taken during the round: never copied into the ballot automatically. */
  speakerNotes: z.record(z.string(), SpeakerNoteSchema).default({}),
  /** Private notes on the round as a whole (not on one debater). */
  roundNotes: z.string().max(8000, 'Round notes can hold up to 8,000 characters.').default(''),
  /** The AI's map of the flow (Decide, step 1). The judge's calls stay in `assessments`. */
  argumentMap: z.object({ createdAt: timestamp, arguments: z.array(MapArgumentSchema).max(60) }).nullable().optional(),
  /** Ballot assistant conversation (drafts, changes, critique), kept with the round. */
  assistantChat: z.array(ChatMessageSchema).max(80).optional(),
  submittedAt: timestamp.nullable(),
  updatedAt: timestamp,
});

export const SettingsSchema = z.object({
  id: z.literal('settings'),
  currentTournamentId: id.nullable(),
  /** Seconds left at which the timer beeps; 0 = time's up. Empty = silent. */
  timerAlertsSec: z.array(z.number().int().min(0).max(600)).max(8),
  timerVolume: z.number().min(0).max(1),
  purgeNamesAfterHours: z.number().int().min(0).max(24 * 30),
  flowFontScale: z.number().min(0.7).max(1.8),
  /** single: one sheet, every speech in order. perCase: a sheet per team's case (the paper convention). */
  flowLayout: z.enum(['single', 'perCase']),
  /** Interface font (the web app's registry names them). 'system' is the device's own. Synced: pick it once. */
  uiFont: z.enum(['system', 'inter', 'plex', 'source', 'atkinson']).default('system'),
  /** Where the judge writes (the flow, notes, ballot): the interface font, another sans, or a writing/mono font. */
  writingFont: z.enum(['inherit', 'inter', 'plex', 'source', 'atkinson', 'quattro', 'plex-mono', 'jetbrains']).default('inherit'),
});

export type Tournament = z.infer<typeof TournamentSchema>;
export type Team = z.infer<typeof TeamSchema>;
export type Round = z.infer<typeof RoundSchema>;
export type RoundStatus = z.infer<typeof RoundStatus>;
export type RoundNames = z.infer<typeof RoundNamesSchema>;
export type Mark = z.infer<typeof Mark>;
export type FlowRow = z.infer<typeof FlowRowSchema>;
export type FlowCell = z.infer<typeof FlowCellSchema>;
export type Clock = z.infer<typeof ClockSchema>;
export type TimerState = z.infer<typeof TimerStateSchema>;
export type TimerRecord = z.infer<typeof TimerRecordSchema>;
export type Survival = z.infer<typeof Survival>;
export type Ballot = z.infer<typeof BallotSchema>;
export type Settings = z.infer<typeof SettingsSchema>;

/** Form input for quickly adding a round. Everything but the two team codes is optional. */
export const NewRoundInputSchema = z.object({
  label: z.string().trim().min(1).max(20),
  room: z.string().trim().max(30).default(''),
  flight: z.string().trim().max(4).default(''),
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Start time must look like 09:30.').or(z.literal('')).default(''),
  teamA: TeamSchema,
  teamB: TeamSchema,
  teamANames: z.array(z.string().trim().max(60)).max(5).default([]),
  teamBNames: z.array(z.string().trim().max(60)).max(5).default([]),
  resolution: z.string().trim().max(1000, 'A resolution can be up to 1,000 characters.').default(''),
}).superRefine((r, ctx) => {
  if (r.teamA.code && r.teamB.code && r.teamA.code.toLowerCase() === r.teamB.code.toLowerCase()) {
    ctx.addIssue({ code: 'custom', path: ['teamB', 'code'], message: 'The two teams have the same code. Check the pairing.' });
  }
});
export type NewRoundInput = z.input<typeof NewRoundInputSchema>;

export const newRoundLabels = {
  label: 'Round',
  room: 'Room',
  flight: 'Flight',
  startTime: 'Start time',
  'teamA.code': 'Team A code',
  'teamB.code': 'Team B code',
  'teamA.school': 'Team A school',
  'teamB.school': 'Team B school',
  'teamANames.*': 'Debater name',
  'teamBNames.*': 'Debater name',
  resolution: 'Resolution',
};

/** Backup file: everything on this device, validated on import with every problem reported. */
export const BackupSchema = z.object({
  app: z.literal('flow'),
  backupVersion: z.literal(1),
  exportedAt: timestamp,
  tournaments: z.array(TournamentSchema),
  rounds: z.array(RoundSchema),
  names: z.array(RoundNamesSchema),
  rows: z.array(FlowRowSchema),
  cells: z.array(FlowCellSchema),
  timers: z.array(TimerRecordSchema),
  ballots: z.array(BallotSchema),
});
export type Backup = z.infer<typeof BackupSchema>;
